import type { ConveyedPersonAction } from "../types/involvement.js";
import type { CustomerRole } from "../types/thalamos.js";

/**
 * What each role, or `none` for a guest, permits of what an involvement
 * conveys (`personActionsConveyedByInvolvementKind`). What a party may do is
 * the intersection, so an involvement never conveys more than the desk
 * somebody is sitting at.
 *
 * `merge` is permitted by no role: it changes what every reader of either
 * person record sees, and is the one change a later correction cannot
 * overwrite, so it comes only from organisation administration
 * (`personActionsFromOrganisationAdministration`).
 *
 * `read_log` goes only to the roles that run a case or administer persons'
 * records, and never to a guest, for a different reason: it is not about the
 * person at all. Who has been reaching a person is the question the
 * organisation accountable for them has to answer, and answering it is not
 * part of assessing, reviewing or looking.
 *
 * `none` is a guest, a user holding no role anywhere. They are narrowed by
 * nothing, because their involvement is the whole of their authority and there
 * is no organisation behind it to hold a role at.
 */
export const personActionsPermittedByRoleOrNone: Record<
  CustomerRole | "none",
  readonly ConveyedPersonAction[]
> = {
  // Reading, as far as the organisation's part in the person's care lets
  // anybody there read, and what an organisation admin who is the person's
  // responsible user does in the case. Everything else an organisation admin
  // does to a person is `personActionsFromOrganisationAdministration`'s, so it
  // ends when the organisation stops administering the person rather than
  // lasting as long as some involvement the organisation holds.
  organisation_admin: ["read", "read_assessments", "read_log", "assign"],
  // Runs the case, so decides who else is involved.
  responsible_user: [
    "read",
    "read_assessments",
    "read_log",
    "update",
    "approve",
    "assign",
    "allocate_responsible_user",
    "involve_guest",
  ],
  assessor: ["read", "read_assessments", "update", "assess", "involve_guest"],
  // Reviews somebody else's work, so neither corrects the person nor assesses.
  approver: ["read", "read_assessments", "approve", "involve_guest"],
  viewer: ["read", "read_assessments"],
  none: ["read", "read_assessments", "assess"],
};
